import { envValue, type EnvRecord } from "./envPreflight";

/**
 * Loop item L10: the codes a visit intent is recorded with, shared by the
 * detector (packages/pipeline), the worker task that writes the event
 * (packages/agents), the call list's reader (packages/db) and the lead record
 * (leadQuality.ts). Here because core is the one package all four import.
 *
 * A `consult.intent` event says the customer's own message read as meaning to
 * come in, to book a consult or appointment, or to be called. It is a reading
 * of words, never a booking: appointments.ts holds that a customer writing
 * "let's do Tuesday at 2" is text, not a booking, and the event is shown on
 * the lead record and ranks nothing (Kabir decides whether it ever does).
 *
 * Codes only, never the message: the event carries a kind from this list and
 * the platform message id, and section 10 keeps the words out.
 */

export const CONSULT_INTENT_EVENT = "consult.intent";

/** Strongest first: a message that says more than one is named by the first of these it says. */
export const CONSULT_INTENT_KINDS = ["consult", "visit", "call"] as const;

export type ConsultIntentKind = (typeof CONSULT_INTENT_KINDS)[number];

/** A kind read back from a jsonb payload, which nothing validated on the way in. */
export function isConsultIntentKind(v: unknown): v is ConsultIntentKind {
  return typeof v === "string" && (CONSULT_INTENT_KINDS as readonly string[]).includes(v);
}

/**
 * L10 review 3. The flag, read by BOTH processes, which is why it lives here
 * and not in either app (`apps/api/src/consultIntentPlan.ts` and
 * `apps/worker/src/consultIntentPlan.ts` re-export this one rule, so the two
 * can never read the same variable two ways).
 *
 *  - The API reads it to decide whether the webhook route QUEUES the read
 *    (`packages/db/src/ingest.ts`).
 *  - The worker reads it to decide whether a queued read WRITES its event
 *    (`packages/agents/src/consultIntentHandler.ts`). Off, the handler
 *    completes each task with a `consult.intent.skipped` row (reason
 *    flag_off) and records nothing, so turning the flag off stops writes even
 *    for the reads queued while it was on, and no task is ever dead-lettered
 *    for want of a handler.
 *
 * Shadow first: off is the default and every deployment today. On means
 * exactly "1" in both. Turning it on is Kabir's.
 *
 *  - ACKRA_ENABLE_CONSULT_INTENT is on only when it is exactly "1". Unset,
 *    empty or "0" is off. Anything else ("true", "yes", " 1") throws, the way
 *    ACKRA_ENABLE_MEMORY_WRITES does: an intent this code cannot read is never
 *    guessed on (section 0).
 *
 * Each app's `envSpec.ts` refuses the same shapes in its preflight, first and
 * all at once. This is the backstop that also returns the value each uses.
 */

export const ENABLE_CONSULT_INTENT_ENV = "ACKRA_ENABLE_CONSULT_INTENT";

/** Null when the value is a valid flag, or the problem sentence. Never quotes the value. */
export function validateEnableConsultIntent(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (the consult intent read on) or "0" (off). Any other value is refused rather than read as on';
}

/** True when the flag, read as set, is exactly "1". */
export function consultIntentRequested(env: EnvRecord): boolean {
  return envValue(env, ENABLE_CONSULT_INTENT_ENV) !== undefined && env[ENABLE_CONSULT_INTENT_ENV] === "1";
}

export function planConsultIntent(env: EnvRecord): boolean {
  if (envValue(env, ENABLE_CONSULT_INTENT_ENV) === undefined) return false;
  const complaint = validateEnableConsultIntent(env[ENABLE_CONSULT_INTENT_ENV] ?? "");
  if (complaint !== null) throw new Error(`${ENABLE_CONSULT_INTENT_ENV} ${complaint}.`);
  return env[ENABLE_CONSULT_INTENT_ENV] === "1";
}
